import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap, Sparkles, Loader2, Newspaper, Clapperboard, MapPin, Trophy, Cloud, Car,
  Flame, ListChecks, Rss, Mic, FileEdit, BookOpen, Palette, Mail, TrendingUp, ArrowUpRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StoryRow } from "@/components/newsroom/StoryRow";

const SECTIONS = [
  { id: "music", label: "Music News", icon: Newspaper, tint: "text-pink-300 bg-pink-500/10" },
  { id: "entertainment", label: "Entertainment", icon: Clapperboard, tint: "text-violet-300 bg-violet-500/10" },
  { id: "local", label: "Local News", icon: MapPin, tint: "text-sky-300 bg-sky-500/10" },
  { id: "sports", label: "Sports", icon: Trophy, tint: "text-amber-300 bg-amber-500/10" },
  { id: "weather", label: "Weather", icon: Cloud, tint: "text-cyan-300 bg-cyan-500/10" },
  { id: "traffic", label: "Traffic", icon: Car, tint: "text-orange-300 bg-orange-500/10" },
];

const DESKS = [
  { to: "/dashboard/news-ingestion", label: "News Ingestion", icon: Rss },
  { to: "/dashboard/news-studio", label: "News Studio", icon: Mic },
  { to: "/dashboard/news", label: "AI Music News", icon: Newspaper },
  { to: "/dashboard/rss-feeds", label: "RSS Feeds", icon: Rss },
  { to: "/dashboard/blog-connector", label: "Blog Connector", icon: FileEdit },
  { to: "/dashboard/writers-desk", label: "Editorial Desk", icon: BookOpen },
  { to: "/dashboard/marketing-kit", label: "Marketing Kit", icon: Palette },
  { to: "/dashboard/newsletter", label: "Newsletter", icon: Mail },
  { to: "/dashboard/top-charts", label: "Top Charts", icon: TrendingUp },
];

const Stat = ({ value, label, tint, testid }) => (
  <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid={testid}>
    <div className={`font-display text-3xl font-bold ${tint}`}>{value}</div>
    <div className="text-xs text-ink-muted mt-1">{label}</div>
  </div>
);

export const NewsroomTab = ({ summary, refresh, setTab }) => {
  const [fetching, setFetching] = useState(false);

  const fetchLatest = async () => {
    setFetching(true);
    try {
      const r = await api.post("/news/ingest");
      toast.success(`Fetched ${r.data.inserted} new stories (${r.data.skipped} already in)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch failed");
    } finally { setFetching(false); }
  };

  if (!summary) return <div className="py-16 text-center text-sm text-ink-muted">Loading newsroom…</div>;
  const t = summary.totals;

  return (
    <div className="space-y-8" data-testid="newsroom-tab">
      <div className="rounded-2xl border border-emerald-500/25 bg-gradient-to-r from-emerald-500/10 to-transparent p-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300">
            <Zap className="h-3.5 w-3.5" /> Virtual Newsroom Active
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-ink-primary mt-1">{summary.date}</h2>
          <p className="text-xs text-ink-muted mt-1">Real sources · Billboard · Rolling Stone · Pitchfork · Google News · Apple Music · Open-Meteo</p>
        </div>
        <button
          onClick={fetchLatest} disabled={fetching} data-testid="newsroom-fetch-latest-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 px-5 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60"
        >
          {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Fetch Latest News
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat value={t.total} label="Total Stories" tint="text-ink-primary" testid="newsroom-stat-total" />
        <Stat value={t.drafts} label="Pending Approval" tint="text-amber-300" testid="newsroom-stat-drafts" />
        <Stat value={t.published} label="Published" tint="text-emerald-400" testid="newsroom-stat-published" />
        <Stat value={t.breaking} label="Breaking Alerts (24h)" tint="text-rose-400" testid="newsroom-stat-breaking" />
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">News Sections</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => setTab(s.id)} data-testid={`newsroom-section-${s.id}`}
              className="rounded-xl border border-line bg-bg-surface p-4 text-center hover:border-brand-amber/50 transition">
              <div className={`mx-auto h-10 w-10 rounded-lg flex items-center justify-center ${s.tint}`}>
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-2 text-xs font-semibold text-ink-primary">{s.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="type-section-heading text-ink-primary">Recent Stories</h3>
            <span className="text-[10px] font-mono text-ink-muted">{t.total} total</span>
          </div>
          <div className="space-y-3">
            {summary.recent.length === 0 && (
              <div className="rounded-xl border border-dashed border-line p-10 text-center text-sm text-ink-muted">
                No stories yet — hit "Fetch Latest News" to pull real music news in.
              </div>
            )}
            {summary.recent.map((s) => <StoryRow key={s.story_id} s={s} />)}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="newsroom-trending-widget">
            <div className="flex items-center gap-2 mb-3">
              <Flame className="h-4 w-4 text-orange-400" />
              <span className="text-sm font-bold text-ink-primary">Trending Artist Tracker</span>
            </div>
            {(summary.trending || []).length === 0 ? (
              <div className="text-xs text-ink-muted py-4 text-center">Apple Music chart unavailable right now</div>
            ) : (
              <div className="space-y-2">
                {summary.trending.map((x) => (
                  <div key={x.rank} className="flex items-center gap-2.5 text-xs" data-testid={`trending-${x.rank}`}>
                    <span className="font-mono text-ink-muted w-4">{x.rank}</span>
                    {x.artwork && <img src={x.artwork} alt="" className="h-7 w-7 rounded" />}
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-semibold text-ink-primary">{x.name}</span>
                      <span className="block truncate text-ink-muted">{x.artist}</span>
                    </span>
                  </div>
                ))}
                <div className="pt-1 text-[9px] font-mono uppercase tracking-wider text-ink-muted">Apple Music · most played · live data</div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="newsroom-pipeline-widget">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks className="h-4 w-4 text-sky-300" />
              <span className="text-sm font-bold text-ink-primary">Content Pipeline</span>
            </div>
            {[["Awaiting Script", summary.pipeline.awaiting_script], ["Awaiting Blog", summary.pipeline.awaiting_blog], ["Awaiting Social", summary.pipeline.awaiting_social]].map(([l, v]) => (
              <div key={l} className="flex justify-between py-1.5 text-xs border-b border-line/50 last:border-0">
                <span className="text-ink-secondary">{l}</span>
                <span className="font-mono font-bold text-brand-amber">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Your Desks</div>
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
          {DESKS.map((d) => (
            <Link key={d.to} to={d.to} data-testid={`newsroom-desk-${d.to.split("/").pop()}`}
              className="group rounded-lg border border-line bg-bg-surface p-3 text-center hover:border-brand-amber/50 transition">
              <d.icon className="h-4 w-4 mx-auto text-ink-muted group-hover:text-brand-amber transition" />
              <div className="mt-1.5 text-[10px] text-ink-secondary leading-tight">{d.label}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};
